// Drives the calendar behind `<.date_picker>`. The hidden inputs are the value;
// everything visible here is drawn from them and written back on apply.
//
// The grid and range maths are exported on their own so they can be tested
// without a DOM.

const DAY_NAMES = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// A range picked without touching the times covers its days end to end.
export const DAY_START = "00:00";
export const DAY_END = "23:59";

const pad = (n) => String(n).padStart(2, "0");

export const toISODate = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

export const fromISODate = (iso) => {
  const [year, month, day] = iso.split("-").map(Number);
  return new Date(year, month - 1, day);
};

// "2026-08-15" or "2026-08-15T09:30" -> { date, time }. The time half is
// optional throughout: a range nobody narrowed within its days has none.
export function parseValue(value) {
  if (!value) return null;

  const [date, time] = String(value).split("T");
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;

  return { date, time: /^\d{2}:\d{2}/.test(time || "") ? time.slice(0, 5) : null };
}

export const formatValue = (date, time) => (time ? `${date}T${time}` : date);

// Six weeks of dates, always — a month-length grid would jump height as you
// page through the year.
export function monthMatrix(year, month) {
  const first = new Date(year, month, 1);
  const start = new Date(year, month, 1 - first.getDay());

  return Array.from({ length: 42 }, (_, i) => {
    const date = new Date(start);
    date.setDate(start.getDate() + i);
    return date;
  });
}

// Years are paged in fixed blocks rather than around the year in view, so
// stepping back and forward returns to the same block instead of drifting.
export const YEARS_PER_PAGE = 12;

export function yearPage(year) {
  const start = Math.floor(year / YEARS_PER_PAGE) * YEARS_PER_PAGE;
  return Array.from({ length: YEARS_PER_PAGE }, (_, i) => start + i);
}

// Selecting a range back-to-front is a normal way to point at two days, so the
// ends are ordered here rather than rejected.
export function orderRange(from, to) {
  if (!from || !to) return { from, to };
  return from <= to ? { from, to } : { from: to, to: from };
}

export const withinRange = (iso, from, to) => Boolean(from && to && iso > from && iso < to);

export function formatLabel({ from, to, mode }) {
  if (!from) return null;

  const one = (value) => {
    const parsed = parseValue(value);
    if (!parsed) return "";

    const label = fromISODate(parsed.date).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });

    return parsed.time ? `${label}, ${parsed.time}` : label;
  };

  if (mode !== "range") return one(from);

  return to ? `${one(from)} → ${one(to)}` : `${one(from)} → …`;
}

export const DatePicker = {
  mounted() {
    this.mode = this.el.dataset.mode || "single";
    this.withTime = this.el.dataset.time === "true";
    this.placeholder = this.el.dataset.placeholder || "Select a date";
    this.min = this.el.dataset.min || null;
    this.max = this.el.dataset.max || null;

    this.fromInput = this.el.querySelector(".dp-from");
    this.toInput = this.el.querySelector(".dp-to");
    this.trigger = this.el.querySelector(".dp-trigger");
    this.panel = this.el.querySelector(".dp-panel");
    this.labelEl = this.el.querySelector(".dp-label");
    this.clearEl = this.el.querySelector(".dp-clear");

    if (!this.fromInput || !this.trigger || !this.panel) return;

    this.readFromInputs();
    this.cursor = this.initialCursor();
    this.view = "days";

    this.boundTriggerClick = this.onTriggerClick.bind(this);
    this.boundPanelClick = this.onPanelClick.bind(this);
    this.boundPanelInput = this.onPanelInput.bind(this);
    this.boundDocumentClick = this.onDocumentClick.bind(this);
    this.boundKeyDown = this.onKeyDown.bind(this);

    this.trigger.addEventListener("click", this.boundTriggerClick);
    this.panel.addEventListener("click", this.boundPanelClick);
    this.panel.addEventListener("input", this.boundPanelInput);
    this.panel.addEventListener("change", this.boundPanelInput);
    document.addEventListener("click", this.boundDocumentClick, true);
    document.addEventListener("keydown", this.boundKeyDown);

    // The server still owns the value: Reset Filters and remembered filters
    // both rewrite these inputs, and the panel would otherwise go stale
    // behind its phx-update="ignore".
    this.observer = new MutationObserver(() => {
      if (this.writing) return;
      this.readFromInputs();
      this.cursor = this.initialCursor();
      this.renderLabel();
      if (this.isOpen()) this.renderPanel();
    });

    this.observeInputs();
    this.renderLabel();
  },

  destroyed() {
    this.trigger?.removeEventListener("click", this.boundTriggerClick);
    this.panel?.removeEventListener("click", this.boundPanelClick);
    this.panel?.removeEventListener("input", this.boundPanelInput);
    this.panel?.removeEventListener("change", this.boundPanelInput);
    document.removeEventListener("click", this.boundDocumentClick, true);
    document.removeEventListener("keydown", this.boundKeyDown);
    this.observer?.disconnect();
  },

  observeInputs() {
    const options = { attributes: true, attributeFilter: ["value"] };
    this.observer.observe(this.fromInput, options);
    if (this.toInput) this.observer.observe(this.toInput, options);
  },

  readFromInputs() {
    const from = parseValue(this.fromInput.value);
    const to = this.toInput ? parseValue(this.toInput.value) : null;

    this.from = from?.date || null;
    this.to = to?.date || null;
    this.fromTime = from?.time || null;
    this.toTime = to?.time || null;
  },

  initialCursor() {
    const anchor = this.from ? fromISODate(this.from) : new Date();
    return { year: anchor.getFullYear(), month: anchor.getMonth() };
  },

  isOpen() {
    return this.trigger.getAttribute("aria-expanded") === "true";
  },

  // Single dates with no time commit the moment one is clicked; anything with
  // a second end or a time needs the panel to stay put while it's filled in.
  needsApply() {
    return this.mode === "range" || this.withTime;
  },

  onTriggerClick(event) {
    if (this.clearEl && this.clearEl.contains(event.target)) {
      event.preventDefault();
      event.stopPropagation();
      this.clear();
      return;
    }

    event.preventDefault();
    this.isOpen() ? this.close() : this.open();
  },

  open() {
    this.readFromInputs();
    this.cursor = this.initialCursor();
    this.view = "days";
    this.renderPanel();
    this.trigger.setAttribute("aria-expanded", "true");

    this.panel.classList.remove("invisible", "opacity-0", "-translate-y-1");
    this.panel.classList.add("opacity-100", "translate-y-0");
  },

  close() {
    this.trigger.setAttribute("aria-expanded", "false");
    this.panel.classList.remove("opacity-100", "translate-y-0");
    this.panel.classList.add("opacity-0", "-translate-y-1");

    // Kept out of the layout once it has faded, so it can't catch clicks.
    this.hideTimer = setTimeout(() => {
      if (!this.isOpen()) this.panel.classList.add("invisible");
    }, 150);
  },

  onDocumentClick(event) {
    if (!this.isOpen()) return;
    if (this.el.contains(event.target)) return;
    this.close();
  },

  onKeyDown(event) {
    if (event.key === "Escape" && this.isOpen()) this.close();
  },

  onPanelClick(event) {
    const step = event.target.closest("[data-step]");
    if (step) {
      event.preventDefault();
      this.step(Number(step.dataset.step));
      return;
    }

    const view = event.target.closest("[data-view]");
    if (view) {
      event.preventDefault();
      this.showView(view.dataset.view);
      return;
    }

    const month = event.target.closest("[data-month]");
    if (month) {
      event.preventDefault();
      this.pickMonth(Number(month.dataset.month));
      return;
    }

    const year = event.target.closest("[data-year]");
    if (year) {
      event.preventDefault();
      this.pickYear(Number(year.dataset.year));
      return;
    }

    const day = event.target.closest("[data-date]");
    if (day) {
      event.preventDefault();
      this.selectDate(day.dataset.date);
      return;
    }

    if (event.target.closest("[data-apply]")) {
      event.preventDefault();
      this.commit();
      this.close();
      return;
    }

    if (event.target.closest("[data-clear]")) {
      event.preventDefault();
      this.clear();
    }
  },

  // Typing a time is local, in-progress state until Apply commits it — left to
  // bubble, it would reach the LiveView form as a premature phx-change with
  // the still-uncommitted value, round-tripping the server and tearing this
  // panel down mid-edit.
  onPanelInput(event) {
    const input = event.target.closest("[data-time-for]");
    if (!input) return;

    event.stopPropagation();

    if (input.dataset.timeFor === "from") this.fromTime = input.value || null;
    else this.toTime = input.value || null;
  },

  // The chevrons move by whatever the grid is showing: a month at a time in the
  // day grid, a year in the month grid, a page of years in the year grid.
  step(direction) {
    const by = {
      days: [0, direction],
      months: [direction, 0],
      years: [direction * YEARS_PER_PAGE, 0],
    }[this.view];
    const cursor = new Date(this.cursor.year + by[0], this.cursor.month + by[1], 1);

    this.cursor = { year: cursor.getFullYear(), month: cursor.getMonth() };
    this.renderPanel();
  },

  showView(view) {
    this.view = view;
    this.renderPanel();
  },

  // Picking either half drops straight back to the days, so changing just the
  // year doesn't cost a second trip through the months.
  pickMonth(month) {
    this.cursor = { ...this.cursor, month };
    this.showView("days");
  },

  pickYear(year) {
    this.cursor = { ...this.cursor, year };
    this.showView("days");
  },

  outOfRange(iso) {
    return Boolean((this.min && iso < this.min) || (this.max && iso > this.max));
  },

  selectDate(iso) {
    if (this.outOfRange(iso)) return;

    if (this.mode !== "range") {
      this.from = iso;
      // Callers asking for a time want a datetime back, so the day opens at
      // midnight until someone says otherwise.
      if (this.withTime && !this.fromTime) this.fromTime = DAY_START;
      this.renderPanel();
      if (!this.needsApply()) {
        this.commit();
        this.close();
      }
      return;
    }

    // A completed range restarts from the day just clicked, so you can drag a
    // new window without clearing the old one first.
    if (!this.from || this.to) {
      this.from = iso;
      this.to = null;
      if (this.withTime) {
        this.fromTime = DAY_START;
        this.toTime = null;
      }
    } else {
      const ordered = orderRange(this.from, iso);
      this.from = ordered.from;
      this.to = ordered.to;
      // The default covers the days whole, so a range picked without touching
      // the times means all of the first day through all of the last. Both
      // stay editable; ordering the ends leaves each default on its own end.
      if (this.withTime) this.toTime = DAY_END;
    }

    this.renderPanel();
  },

  clear() {
    this.from = null;
    this.to = null;
    this.fromTime = null;
    this.toTime = null;
    this.commit();
    this.renderLabel();
    if (this.isOpen()) this.renderPanel();
  },

  commit() {
    this.write(this.fromInput, this.from ? formatValue(this.from, this.fromTime) : "");
    if (this.toInput) this.write(this.toInput, this.to ? formatValue(this.to, this.toTime) : "");

    this.renderLabel();
    this.fromInput.dispatchEvent(new Event("input", { bubbles: true }));
  },

  // The attribute is set alongside the property so the server sees the same
  // value it would from a native control — and `writing` keeps the observer
  // above from treating our own write as someone else's.
  write(input, value) {
    this.writing = true;
    input.value = value;
    input.setAttribute("value", value);
    this.writing = false;
  },

  renderLabel() {
    const label = formatLabel({
      from: this.labelValue("from"),
      to: this.labelValue("to"),
      mode: this.mode,
    });

    this.labelEl.textContent = label || this.placeholder;
    this.clearEl?.classList.toggle("hidden", !label);
  },

  labelValue(half) {
    const date = half === "from" ? this.from : this.to;
    const time = half === "from" ? this.fromTime : this.toTime;
    return date ? formatValue(date, time) : null;
  },

  renderPanel() {
    // One fixed height across all three grids, so swapping views doesn't make
    // the panel jump under the pointer.
    this.panel.innerHTML = `
      ${this.headerHTML()}
      <div class="h-[14.75rem]">${this.viewHTML()}</div>
      ${this.withTime ? this.timesHTML() : ""}
      ${this.footerHTML()}
    `;
  },

  headerHTML() {
    const stepButton = (step, icon, label) => `
      <button type="button" data-step="${step}" aria-label="${label}"
        class="rounded-md p-1 text-content-subtle transition-colors duration-150 hover:bg-surface-hover hover:text-content">
        <span class="${icon} size-4 block"></span>
      </button>`;

    // Carries no background of its own, so the header reads exactly as it did
    // before it was clickable — the hover state is the only affordance.
    const pick = (view, text) => `
      <button type="button" data-view="${view}"
        class="rounded-md px-1.5 py-0.5 text-sm font-semibold text-content transition-colors duration-150 hover:bg-surface-hover">
        ${text}
      </button>`;

    const title = {
      days: `${pick("months", MONTH_NAMES[this.cursor.month])}${pick("years", this.cursor.year)}`,
      months: pick("years", this.cursor.year),
      years: `<span class="px-1.5 text-sm font-semibold text-content">${this.yearRangeLabel()}</span>`,
    }[this.view];

    return `
      <div class="mb-2 flex items-center justify-between gap-2">
        ${stepButton(-1, "hero-chevron-left", this.stepLabel("Previous"))}
        <div class="flex items-center gap-0.5">${title}</div>
        ${stepButton(1, "hero-chevron-right", this.stepLabel("Next"))}
      </div>`;
  },

  yearRangeLabel() {
    const years = yearPage(this.cursor.year);
    return `${years[0]} – ${years[years.length - 1]}`;
  },

  stepLabel(direction) {
    return `${direction} ${{ days: "month", months: "year", years: "years" }[this.view]}`;
  },

  viewHTML() {
    if (this.view === "months") return this.monthsHTML();
    if (this.view === "years") return this.yearsHTML();
    return this.gridHTML();
  },

  // Months and years share a shape: four rows of three, filling the same box
  // the day grid occupies.
  choiceGridHTML(choices) {
    const cells = choices
      .map(
        ({ attr, label, selected }) => `
        <button type="button" ${attr}
          class="grid place-items-center rounded-md text-sm transition-colors duration-150 ${
            selected
              ? "bg-indigo-600 font-semibold text-white"
              : "text-content hover:bg-surface-hover"
          }">${label}</button>`,
      )
      .join("");

    return `<div class="grid h-full grid-cols-3 grid-rows-4 gap-1">${cells}</div>`;
  },

  monthsHTML() {
    return this.choiceGridHTML(
      MONTH_NAMES.map((name, month) => ({
        attr: `data-month="${month}"`,
        label: name.slice(0, 3),
        selected: month === this.cursor.month,
      })),
    );
  },

  yearsHTML() {
    return this.choiceGridHTML(
      yearPage(this.cursor.year).map((year) => ({
        attr: `data-year="${year}"`,
        label: year,
        selected: year === this.cursor.year,
      })),
    );
  },

  gridHTML() {
    const today = toISODate(new Date());

    const heads = DAY_NAMES.map(
      (day) =>
        `<div class="grid size-8 place-items-center text-xs font-medium text-content-subtle">${day}</div>`,
    ).join("");

    const cells = monthMatrix(this.cursor.year, this.cursor.month)
      .map((date) => this.dayHTML(date, today))
      .join("");

    return `<div class="grid grid-cols-7 gap-y-0.5">${heads}${cells}</div>`;
  },

  dayHTML(date, today) {
    const iso = toISODate(date);
    const outside = date.getMonth() !== this.cursor.month;
    const isFrom = iso === this.from;
    const isTo = iso === this.to;
    const isEnd = isFrom || isTo;
    const inside = withinRange(iso, this.from, this.to);
    const barred = this.outOfRange(iso);

    // The band that joins a range is painted on the wrapper so it meets its
    // neighbours edge to edge, while the day itself keeps its own rounding.
    const band = [
      inside && "bg-indigo-500/15",
      isFrom && this.to && "bg-indigo-500/15 rounded-l-full",
      isTo && "bg-indigo-500/15 rounded-r-full",
    ]
      .filter(Boolean)
      .join(" ");

    const day = [
      "grid size-8 place-items-center rounded-full text-sm transition-colors duration-150",
      barred && "cursor-not-allowed text-content-subtle/40",
      !barred && isEnd && "bg-indigo-600 font-semibold text-white",
      !barred && !isEnd && "hover:bg-surface-hover",
      !barred && !isEnd && outside && "text-content-subtle/60",
      !barred && !isEnd && !outside && "text-content",
      !barred &&
        !isEnd &&
        iso === today &&
        "font-semibold text-indigo-500 ring-1 ring-indigo-500/40",
    ]
      .filter(Boolean)
      .join(" ");

    const attrs = barred ? "disabled" : `data-date="${iso}"`;

    return `<div class="${barred ? "" : band}"><button type="button" ${attrs} class="${day}">${date.getDate()}</button></div>`;
  },

  // A row apiece, label beside input: the range labels say which end they set
  // in full, which no longer fits above two inputs sharing the panel's width.
  timesHTML() {
    const field = (half, label, value) => `
      <label class="flex items-center justify-between gap-3">
        <span class="text-xs font-medium uppercase tracking-wide text-content-subtle">${label}</span>
        <input type="time" data-time-for="${half}" value="${value || ""}"
          class="h-8 w-24 shrink-0 rounded-md border border-line-strong bg-surface px-2 text-sm text-content
                 [color-scheme:light] dark:[color-scheme:dark]
                 focus:outline-2 focus:outline-offset-0 focus:outline-zinc-400 dark:focus:outline-cyan-500" />
      </label>`;

    const fields =
      this.mode === "range"
        ? `${field("from", "Range start time", this.fromTime)}${field("to", "Range end time", this.toTime)}`
        : field("from", "Time", this.fromTime);

    return `<div class="mt-3 flex flex-col gap-2 border-t border-line-strong pt-3">${fields}</div>`;
  },

  footerHTML() {
    const apply = this.needsApply()
      ? `<button type="button" data-apply
           class="rounded-md bg-indigo-600 px-2.5 py-1 text-sm font-medium text-white transition-colors duration-150 hover:bg-indigo-500">
           Apply
         </button>`
      : "";

    return `
      <div class="mt-3 flex items-center justify-between gap-2 border-t border-line-strong pt-3">
        <button type="button" data-clear
          class="rounded-md px-2 py-1 text-sm text-content-subtle transition-colors duration-150 hover:bg-surface-hover hover:text-content">
          Clear
        </button>
        ${apply}
      </div>`;
  },
};
